import { useEffect, useState } from "react";
import { Sparkles, TrendingUp } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { insightsApi } from "@/lib/crmApi";

const TONES = {
  high:    { bg: "bg-emerald-50 dark:bg-emerald-950/40", text: "text-emerald-700 dark:text-emerald-300", border: "border-emerald-200 dark:border-emerald-900/40" },
  medium:  { bg: "bg-amber-50 dark:bg-amber-950/40",     text: "text-amber-700 dark:text-amber-300",     border: "border-amber-200 dark:border-amber-900/40" },
  low:     { bg: "bg-zinc-100 dark:bg-zinc-800/60",       text: "text-zinc-700 dark:text-zinc-300",       border: "border-zinc-200 dark:border-zinc-700" },
};

const toneFor = (score) =>
  score >= 75 ? TONES.high : score >= 50 ? TONES.medium : TONES.low;

/**
 * Explainable score badge. Shows the 0-100 score and stars; on click,
 * opens a popover with each factor's contribution and a recommendation.
 *
 * Two ways to use:
 *  1. Pass `insights` directly (already fetched).
 *  2. Pass `leadId` and the component fetches & caches it via React Query.
 */
export default function ScoreBadge({
  insights: insightsProp,
  leadId,
  size = "md",        // "sm" | "md"
  showStars = true,
  className,
  testId,
}) {
  // Optional auto-fetch (no React Query dependency)
  const [fetched, setFetched] = useState(null);
  useEffect(() => {
    if (insightsProp || !leadId) return;
    let cancelled = false;
    insightsApi
      .forLead(leadId)
      .then((d) => { if (!cancelled) setFetched(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [leadId, insightsProp]);

  const insights = insightsProp || fetched;

  if (!insights) {
    // Skeleton placeholder
    return (
      <span
        data-testid={testId || "score-badge-skeleton"}
        className={cn(
          "inline-flex animate-shimmer rounded-full",
          size === "sm" ? "h-5 w-14" : "h-6 w-20",
          className
        )}
      />
    );
  }

  const score = insights.quality_score ?? 0;
  const stars = insights.quality_stars ?? 0;
  const tone = toneFor(score);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId || "score-badge"}
          aria-label={`Score qualité ${score} sur 100, ${stars} étoiles. Cliquer pour voir le détail.`}
          className={cn(
            "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-bold transition-colors hover:brightness-95 cursor-pointer",
            size === "sm" ? "text-[10px]" : "text-xs",
            tone.bg, tone.text, tone.border,
            className
          )}
        >
          <Sparkles className={size === "sm" ? "h-2.5 w-2.5" : "h-3 w-3"} />
          {score}/100
          {showStars && (
            <span className="ml-0.5 opacity-80">
              {"★".repeat(stars)}
              {"☆".repeat(5 - stars)}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-[340px] p-4" data-testid="score-popover">
        <header className="mb-3 flex items-center justify-between">
          <div>
            <p className="text-label text-muted-foreground">Score qualité</p>
            <h4 className="font-heading text-lg font-bold leading-none">
              {score}<span className="text-sm font-normal text-muted-foreground">/100</span>
            </h4>
          </div>
          <div className="text-right">
            <p className="text-label text-muted-foreground">Probabilité</p>
            <div className="font-heading mt-0.5 inline-flex items-center gap-1 text-base font-bold">
              <TrendingUp className="h-3.5 w-3.5 text-accent-blue" />
              {insights.probability ?? 0}%
            </div>
          </div>
        </header>

        <div className="space-y-2.5">
          {(insights.factors || []).map((f) => {
            const earned = Math.round((f.value || 0) * f.weight);
            return (
              <div key={f.key || f.label} data-testid={`factor-${f.key || f.label}`}>
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-foreground">{f.label}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {earned}/{f.weight} pts
                  </span>
                </div>
                <Progress value={(f.value || 0) * 100} className="mt-1 h-1.5" />
                <p className="mt-1 text-[11px] leading-tight text-muted-foreground">
                  {f.explanation}
                </p>
              </div>
            );
          })}
        </div>

        {insights.probability_reason && (
          <p className="mt-3 text-[11px] text-muted-foreground italic">
            {insights.probability_reason}
          </p>
        )}

        {insights.recommendation && (
          <div
            className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-2.5 text-xs dark:border-blue-900/40 dark:bg-blue-950/40"
            data-testid="score-recommendation"
          >
            <span className="font-semibold text-blue-700 dark:text-blue-300">💡 Action conseillée : </span>
            <span className="text-blue-800 dark:text-blue-200">{insights.recommendation}</span>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Lightweight inline badge — no popover, just visual.
 * Useful in dense tables where clicking to expand is overkill.
 */
export function ScoreChip({ score, size = "sm", className }) {
  const tone = toneFor(score || 0);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0 font-bold tabular-nums",
        size === "sm" ? "text-[10px]" : "text-xs",
        tone.bg, tone.text, tone.border,
        className
      )}
    >
      {score ?? 0}
    </span>
  );
}
